import fs from "node:fs/promises";
import path from "node:path";
import { pathToFileURL } from "node:url";
import { COMPLETION_STATUS_TEXT } from "../../core/statuses.js";
export interface TransformedItem {
  content: string;
  name: string;
}

/**
 * Maps OpenCode agent names to primary agent roles.
 */
const PERSONA_MAPPING: Record<string, string> = {
  "atlas": "Atlas",
  "orion": "Orion",
  "sage": "Sage",
  "nexus": "Nexus",
  "astra": "Astra",
  "sage-validation": "Sage",
  "orion-release": "Orion",
  "orion-deploy": "Orion",
  "nexus-discovery": "Nexus",
  "phoenix": "Phoenix"
};

/**
 * Antigravity Adapter - Transforms OpenCode .md to Antigravity Skill format and manages .agents/ integration.
 */
export class AntigravityAdapter {
  cwd: string;

  constructor({ cwd }: { cwd: string }) {
    this.cwd = cwd;
  }

  /**
   * Transforms an OpenCode agent or specialized skill file to an Antigravity Skill.
   */
  async transform(filePath: string): Promise<TransformedItem> {
    const content = await fs.readFile(filePath, "utf8");
    const fileName = path.basename(filePath, ".md") === "SKILL" 
      ? path.basename(path.dirname(filePath)) 
      : path.basename(filePath, ".md");
      
    const persona = PERSONA_MAPPING[fileName.toLowerCase()] || "Astra";
    
    // Check if already has frontmatter
    if (content.trim().startsWith("---")) {
      return { content, name: fileName };
    }

    // Extract description from the first paragraph after the title
    const descriptionMatch = content.match(/^# .*\n\n(.*)/m);
    const description = descriptionMatch ? descriptionMatch[1].trim() : `Specialized agent for ${fileName}.`;

    const skillContent = `---
name: ${fileName}
description: ${description}
---
# ${persona} Persona

${content}

##  Completion Contract (Mandatory)
Every task completion MUST provide the following payload:
1. **Status**: ${COMPLETION_STATUS_TEXT}
2. **Scope reviewed**: Brief summary of what was analyzed.
3. **Findings**: Evidence-based discoveries.
4. **Files affected**: List of all concrete paths modified or created.
5. **Validation/evidence**: Commands run and their results.
6. **Risks/notes**: Any caveats or technical debt introduced.
7. **Required manager action**: Clear next step for the orchestrator.
8. **Can manager continue**: [yes/no]
`;

    return {
      content: skillContent,
      name: fileName
    };
  }

  /**
   * Deploys transformed skills and agents to their respective directories.
   * Also orchestrates the .antigravity/ native integration.
   */
  async deploy(transformedItems: TransformedItem[], installRoot: string = ".ai-workflow"): Promise<void> {
    const agentsDir = path.join(this.cwd, installRoot, "opencode/agents");
    const skillsDir = path.join(this.cwd, installRoot, "opencode/skills");
    
    await fs.mkdir(agentsDir, { recursive: true });
    await fs.mkdir(skillsDir, { recursive: true });

    for (const item of transformedItems) {
      const isAgent = PERSONA_MAPPING[item.name.toLowerCase()];
      const targetDir = isAgent ? agentsDir : path.join(skillsDir, item.name);
      const fileName = isAgent ? `${item.name}.md` : "SKILL.md";
      
      const absoluteTargetDir = isAgent ? agentsDir : targetDir;
      await fs.mkdir(absoluteTargetDir, { recursive: true });
      await fs.writeFile(path.join(absoluteTargetDir, fileName), item.content);
    }
    
    // Deploy native Antigravity extensions (.antigravity/)
    await this.deployNativeExtensions(installRoot);

    // Also deploy root ANTIGRAVITY.md if it doesn't exist
    await this.deployInstructions();
  }

  /**
   * Orchestrates the .agents/ directory with copies of .ai-workflow/ assets.
   * Using hard copies instead of symlinks ensures native discovery across all platforms.
   */
  async deployNativeExtensions(installRoot: string = ".ai-workflow"): Promise<void> {
    const antigravityDir = path.join(this.cwd, ".agents");
    const antigravityAgents = path.join(antigravityDir, "agents");
    const antigravitySkills = path.join(antigravityDir, "skills");
    const antigravityCommands = path.join(antigravityDir, "commands");

    await fs.mkdir(antigravityAgents, { recursive: true });
    await fs.mkdir(antigravitySkills, { recursive: true });
    await fs.mkdir(antigravityCommands, { recursive: true });

    const getUrls = (targetPath: string) => {
      const linuxUrl = pathToFileURL(targetPath).href;
      const urls = [linuxUrl];
      
      if (process.platform === "linux" && process.env.WSL_DISTRO_NAME) {
        const distro = process.env.WSL_DISTRO_NAME;
        const normalizedPath = targetPath.replaceAll("\\", "/");
        urls.push(`file://wsl.localhost/${distro}${normalizedPath}`);
        urls.push(`file:///wsl.localhost/${distro}${normalizedPath}`);
      }
      
      return urls.join("\n");
    };

    const toolNames = [
      "send_message",
      "find_by_name",
      "grep_search",
      "view_file",
      "list_dir",
      "read_url_content",
      "search_web",
      "schedule",
      "multi_replace_file_content",
      "replace_file_content",
      "write_to_file",
      "run_command",
      "manage_task",
      "define_subagent",
      "invoke_subagent",
      "manage_subagents",
      "call_mcp_tool"
    ];

    const systemPromptConfig = {
      "includeSections": [
        "user_information",
        "mcp_servers",
        "skills",
        "subagent_reminder",
        "messaging",
        "artifacts",
        "user_rules"
      ]
    };

    // 1. Deploy Primary Agents to .agents/agents/{agent_name}/
    const sourceAgentsDir = path.join(this.cwd, installRoot, "opencode/agents");
    const agentFiles = await fs.readdir(sourceAgentsDir).catch(() => []);

    const AGENTS_INFO: Record<string, { name: string; description: string }> = {
      "atlas": { name: "Atlas", description: "Router and workflow coordinator for AI Workflow Kit" },
      "nexus": { name: "Nexus", description: "Discovery, requirement, scope, and specification owner" },
      "orion": { name: "Orion", description: "Planning, PR sequencing, release, and deployment strategy owner" },
      "astra": { name: "Astra", description: "Implementation owner for incremental, scoped changes" },
      "sage": { name: "Sage", description: "Audit, validation, evidence, and quality gate owner" },
      "phoenix": { name: "Phoenix", description: "Remediation and regression recovery owner" }
    };

    for (const file of agentFiles) {
      if (!file.endsWith(".md")) continue;
      const lowercaseName = path.basename(file, ".md").toLowerCase();
      const info = AGENTS_INFO[lowercaseName];
      if (!info) continue;

      const agentFolder = path.join(antigravityAgents, lowercaseName);
      await fs.mkdir(agentFolder, { recursive: true });

      // Copy Markdown file to the agent's folder
      const content = await fs.readFile(path.join(sourceAgentsDir, file), "utf8");
      const mdTarget = path.join(agentFolder, `${lowercaseName}.md`);
      await fs.writeFile(mdTarget, content);

      // Generate agent.json
      const agentJson = {
        name: lowercaseName,
        description: info.description,
        hidden: false,
        config: {
          customAgent: {
            systemPromptSections: [
              {
                title: "Agent System Instructions",
                content: `You are ${info.name}, the ${lowercaseName === "astra" ? "implementation" : lowercaseName} owner.\n\nFollow the contract instructions in:\n${getUrls(mdTarget)}`
              }
            ],
            toolNames,
            systemPromptConfig
          }
        }
      };
      await fs.writeFile(path.join(agentFolder, "agent.json"), JSON.stringify(agentJson, null, 2));
    }

    // 2. Deploy Subagents to .agents/agents/{subagent_name}/
    const SUBAGENTS_INFO: Record<string, { description: string; skill: string }> = {
      "Architecture-Specialist": { description: "Select the simplest architecture that safely satisfies current requirements", skill: "architecture" },
      "Backend-Engineer": { description: "Support safe PHP/Node/Python backend changes for APIs and persistence", skill: "backend-development" },
      "Deployment-Specialist": { description: "Plan, review, or validate deployment, release, and production-readiness", skill: "deployment" },
      "Design-Specialist": { description: "Apply practical design principles to keep solutions simple and maintainable", skill: "design-principles" },
      "Docs-Engineer": { description: "Write or improve project documentation, READMEs, guides, and prompts", skill: "documentation" },
      "Frontend-Engineer": { description: "Implement or review frontend changes involving UI, state, routing, and accessibility", skill: "frontend-development" },
      "Full-Stack-Engineer": { description: "Coordinate frontend and backend changes as one coherent, testable increment", skill: "full-stack-development" },
      "Token-Economist": { description: "Reduce context size while preserving information required for safe execution", skill: "optimize-tokens" },
      "PR-Manager": { description: "Plan, implement, review, or validate small pull requests while preserving scope", skill: "pr-workflow" },
      "Discovery-Analyst": { description: "Clarify ambiguous requests into actionable problem statements and success outcomes", skill: "product-discovery" },
      "Product-Planner": { description: "Turn clarified needs into scoped requirements with acceptance-ready outcomes", skill: "product-planning" },
      "Memory-Guardian": { description: "Manage discoverable project memory for durable decisions and recurring corrections", skill: "project-memory" },
      "Prompt-Engineer": { description: "Expert in creating and refining prompts for AI agents and workflows", skill: "prompt-engineer" },
      "QA-Engineer": { description: "Design, review, or improve tests, acceptance criteria, and validation evidence", skill: "qa-workflow" },
      "Refactoring-Specialist": { description: "Improve code/document structure safely while preserving behavior", skill: "refactoring" },
      "Release-Specialist": { description: "Prepare release readiness decisions with explicit gates and evidence", skill: "release-workflow" },
      "SDD-Specialist": { description: "Convert approved requirements into explicit, testable specification artifacts", skill: "spec-driven-development" },
      "Technical-Leader": { description: "Make technical decisions, review architecture, and identify trade-offs", skill: "technical-leadership" },
      "UI-UX-Engineer": { description: "Improve usability and interface clarity without introducing unnecessary complexity", skill: "ui-ux-design" }
    };

    for (const [name, subInfo] of Object.entries(SUBAGENTS_INFO)) {
      const lowercaseSubagentName = name.toLowerCase();
      const subagentFolder = path.join(antigravityAgents, lowercaseSubagentName);
      await fs.mkdir(subagentFolder, { recursive: true });

      const skillPath = path.join(antigravitySkills, subInfo.skill, "SKILL.md");

      const subagentJson = {
        name: lowercaseSubagentName,
        description: subInfo.description,
        hidden: false,
        config: {
          customAgent: {
            systemPromptSections: [
              {
                title: "Agent System Instructions",
                content: `You are ${name}.\n\nFollow the contract instructions in:\n${getUrls(skillPath)}`
              }
            ],
            toolNames,
            systemPromptConfig
          }
        }
      };
      await fs.writeFile(path.join(subagentFolder, "agent.json"), JSON.stringify(subagentJson, null, 2));
    }

    // 3. Copy Skills
    const sourceSkillsDir = path.join(this.cwd, installRoot, "opencode/skills");
    const skillFolders = await fs.readdir(sourceSkillsDir).catch(() => []);
    for (const folder of skillFolders) {
      const skillSource = path.join(sourceSkillsDir, folder, "SKILL.md");
      if (await this.exists(skillSource)) {
        await fs.mkdir(path.join(antigravitySkills, folder), { recursive: true });
        const content = await fs.readFile(skillSource, "utf8");
        await fs.writeFile(path.join(antigravitySkills, folder, "SKILL.md"), content);
      }
    }

    // 4. Copy Commands (from dist-assets/commands)
    const sourceCommandsDir = path.join(this.cwd, installRoot, "opencode/commands");
    if (await this.exists(sourceCommandsDir)) {
      const commandFiles = await fs.readdir(sourceCommandsDir).catch(() => []);
      for (const file of commandFiles) {
        if (!file.endsWith(".md") && !file.endsWith(".toml")) continue;
        const content = await fs.readFile(path.join(sourceCommandsDir, file), "utf8");
        await fs.writeFile(path.join(antigravityCommands, file), content);
      }
    }

    // 5. Copy governance policy into .agents/docs/policies/ so the relative
    //    link ../../docs/policies/SKILLS_COMMON_GOVERNANCE.md in each
    //    .agents/skills/{skill}/SKILL.md resolves correctly.
    const sourcePoliciesDir = path.join(this.cwd, installRoot, "opencode", "docs", "policies");
    const antigravityPolicies = path.join(antigravityDir, "docs", "policies");
    if (await this.exists(sourcePoliciesDir)) {
      await fs.mkdir(antigravityPolicies, { recursive: true });
      const policyFiles = await fs.readdir(sourcePoliciesDir).catch(() => []);
      for (const file of policyFiles) {
        if (!file.endsWith(".md")) continue;
        const content = await fs.readFile(path.join(sourcePoliciesDir, file), "utf8");
        await fs.writeFile(path.join(antigravityPolicies, file), content);
      }
    }

    // 6. Inject project settings.json
    const settingsPath = path.join(antigravityDir, "settings.json");
    if (!(await this.exists(settingsPath))) {
      const settings = {
        model: { name: "auto" },
        ui: { theme: "Ayu" }
      };
      await fs.writeFile(settingsPath, JSON.stringify(settings, null, 2));
    }
  }

  async exists(p: string): Promise<boolean> {
    try {
      await fs.access(p);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Deploys the main ANTIGRAVITY.md and .antigravityignore instructions to the project root.
   */
  async deployInstructions(): Promise<void> {
    const targetPath = path.join(this.cwd, "ANTIGRAVITY.md");
    const ignorePath = path.join(this.cwd, ".antigravityignore");
    
    // Deploy ANTIGRAVITY.md if it doesn't exist
    try {
      await fs.access(targetPath);
    } catch {
      const instructions = `# AI Workflow Kit - Engineering Governance

## Mandate: Atlas Authority Protocol
All architectural changes must align with the specifications issued by the **Specification Authority** (./specs).

## Ternary Architecture
- **Root:** Orchestration and config.
- **.ai-workflow/:** Implementation (The Muscle).
- **./specs:** Specifications (The Brain).

## Branch Gates
- **main Branch:** READ-ONLY.
- **Validation:** Merges require successful observed validation; full/release workflows may persist EVIDENCE.json.

## Primary Agents
- **Atlas (Orchestrator)**
- **Orion (Strategist)**
- **Sage (Auditor)**
- **Nexus (Spec Architect)**
- **Astra (Developer)**
- **Phoenix (Healer)**

## Operational Preferences
- Use \`token-economy\` and \`minimal-context\` skills.
- **Native Discovery**: This project uses workspace-specific extensions in \`.agents/\`.
- **Workflow Entry**: Use \`/atlas\` to begin any task.
`;
      await fs.writeFile(targetPath, instructions);
    }

    // Deploy .antigravityignore if it doesn't exist
    try {
      await fs.access(ignorePath);
    } catch {
      const ignoreContent = `.ai-workflow/
.ai-workflow-backups/
node_modules/
.git/
EVIDENCE.json
*.tgz
*.zip
*.tar
.agents/tmp/
.agents/history/
`;
      await fs.writeFile(ignorePath, ignoreContent);
    }
  }
}
